import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import type { CRMUser, SupplierOrg } from '../../types';
import { listDesigners } from '../../utils/dealDesignerArchitect';
import { listArchitects } from '../../utils/architectProjectAccess';
import { writeCpaPreviewFlag } from '../../utils/cpaPortal';
import {
  adminPortalPreviewOptionValue,
  adminPortalSupplierPreviewOptionValue,
  resolveAdminPortalPreview,
} from '../../utils/adminPortalPreviewTargets';
import { supplierPortalRoleLabel } from '../../utils/supplierWorkFlags';
import './ExternalPortalsPreviewPanel.css';

type Props = {
  crmUsers: CRMUser[];
  supplierOrgs: SupplierOrg[];
  showCpa?: boolean;
};

function PreviewSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="ext-portals-section">
      <h2 className="ext-portals-section-title">{title}</h2>
      <ul className="ext-portals-list">{children}</ul>
    </section>
  );
}

function PreviewRow({ label, value }: { label: string; value: string }) {
  const navigate = useNavigate();

  return (
    <li>
      <button
        type="button"
        className="ext-portals-row"
        onClick={() => {
          const resolution = resolveAdminPortalPreview(value);
          if (!resolution) return;
          if (resolution.type === 'cpa') {
            writeCpaPreviewFlag(true);
          }
          navigate(resolution.href);
        }}
      >
        <span>{label}</span>
        <ChevronRight size={16} aria-hidden />
      </button>
    </li>
  );
}

const ExternalPortalsPreviewPanel: React.FC<Props> = ({
  crmUsers,
  supplierOrgs,
  showCpa = false,
}) => {
  const designers = useMemo(() => listDesigners(crmUsers), [crmUsers]);
  const architects = useMemo(() => listArchitects(crmUsers), [crmUsers]);
  const engineers = useMemo(
    () => crmUsers.filter((u) => u.isActive && u.role === 'engineer')
      .sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [crmUsers],
  );
  const tradeUsers = useMemo(
    () => crmUsers.filter((u) => u.isActive && u.role === 'subcontractor')
      .sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [crmUsers],
  );
  const contractors1099 = useMemo(
    () => crmUsers.filter((u) => u.isActive && u.is1099Contractor === true)
      .sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [crmUsers],
  );
  const sortedOrgs = useMemo(
    () => [...supplierOrgs]
      .filter((o) => o.id)
      .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })),
    [supplierOrgs],
  );

  const hasAny =
    showCpa
    || designers.length > 0
    || architects.length > 0
    || engineers.length > 0
    || sortedOrgs.length > 0
    || tradeUsers.length > 0
    || contractors1099.length > 0;

  if (!hasAny) {
    return (
      <p className="ext-portals-empty">
        No portal previews are available yet. Invite designers, suppliers, or trade partners first.
      </p>
    );
  }

  return (
    <div className="ext-portals-panel">
      {showCpa && (
        <PreviewSection title="CPA">
          <PreviewRow label="CPA portal (finances)" value="cpa" />
        </PreviewSection>
      )}
      {designers.length > 0 && (
        <PreviewSection title="Designers">
          {designers.map((u) => (
            <PreviewRow
              key={u.uid}
              label={u.displayName}
              value={adminPortalPreviewOptionValue('designer', u.uid)}
            />
          ))}
        </PreviewSection>
      )}
      {architects.length > 0 && (
        <PreviewSection title="Architects">
          {architects.map((u) => (
            <PreviewRow
              key={u.uid}
              label={u.displayName}
              value={adminPortalPreviewOptionValue('architect', u.uid)}
            />
          ))}
        </PreviewSection>
      )}
      {engineers.length > 0 && (
        <PreviewSection title="Engineers">
          {engineers.map((u) => (
            <PreviewRow
              key={u.uid}
              label={u.displayName}
              value={adminPortalPreviewOptionValue('engineer', u.uid)}
            />
          ))}
        </PreviewSection>
      )}
      {sortedOrgs.length > 0 && (
        <>
          <PreviewSection title="Supplier portal — owner">
            {sortedOrgs.map((org) => (
              <PreviewRow
                key={`owner-${org.id}`}
                label={org.name || org.id || 'Supplier'}
                value={adminPortalSupplierPreviewOptionValue(org.id!, 'owner')}
              />
            ))}
          </PreviewSection>
          <PreviewSection title="Supplier portal — employee">
            {sortedOrgs.map((org) => (
              <PreviewRow
                key={`employee-${org.id}`}
                label={`${org.name || org.id} · ${supplierPortalRoleLabel('employee')}`}
                value={adminPortalSupplierPreviewOptionValue(org.id!, 'employee')}
              />
            ))}
          </PreviewSection>
        </>
      )}
      {contractors1099.length > 0 && (
        <PreviewSection title="1099 contractor timesheets">
          {contractors1099.map((u) => (
            <PreviewRow
              key={u.uid}
              label={u.displayName}
              value={adminPortalPreviewOptionValue('contractor1099', u.uid)}
            />
          ))}
        </PreviewSection>
      )}
      {tradeUsers.length > 0 && (
        <PreviewSection title="Trade briefings">
          {tradeUsers.map((u) => (
            <PreviewRow
              key={u.uid}
              label={u.displayName}
              value={adminPortalPreviewOptionValue('trade', u.uid)}
            />
          ))}
        </PreviewSection>
      )}
    </div>
  );
};

export default ExternalPortalsPreviewPanel;
